import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";

const initial = { name: "", phone: "", position: "hall", message: "" };

export default function Careers() {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState(initial);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get("/info").then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const buildMessage = () => {
    const lng = i18n.language;
    const intro = lng === "en"
      ? "Hello, I'd like to apply for a position at Harry's Salotto e Cucina. I'll send my CV right after this message."
      : lng === "de"
      ? "Hallo, ich möchte mich bei Harry's Salotto e Cucina bewerben. Den Lebenslauf sende ich gleich im Anschluss."
      : "Salve, vorrei candidarmi per una posizione da Harry's Salotto e Cucina. Invierò il mio CV subito dopo questo messaggio.";
    const labels = lng === "en"
      ? { name: "Name", phone: "Phone", position: "Position", msg: "About me" }
      : lng === "de"
      ? { name: "Name", phone: "Telefon", position: "Position", msg: "Über mich" }
      : { name: "Nome", phone: "Telefono", position: "Posizione", msg: "Su di me" };
    const positionLabel = t(`careers.positions.${form.position}`);
    const lines = [
      intro, "",
      `${labels.name}: ${form.name}`,
      `${labels.phone}: ${form.phone}`,
      `${labels.position}: ${positionLabel}`,
    ];
    if (form.message) lines.push(`${labels.msg}: ${form.message}`);
    return lines.join("\n");
  };

  const submit = (e) => {
    e.preventDefault();
    if (!info?.whatsapp) { toast.error("WhatsApp non configurato"); return; }
    const phone = info.whatsapp.replace(/[^0-9]/g, "");
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(buildMessage())}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(t("careers.success"));
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-[#E5E3D8] focus:border-[#8B3A3A] focus:ring-0 outline-none px-0 py-3 text-base placeholder:text-[#A6A6A0]";

  return (
    <div data-testid="careers-page" className="py-20 sm:py-28">
      <div className="max-w-3xl mx-auto px-6 sm:px-8">
        <p className="text-xs tracking-[0.3em] uppercase text-[#5C5C59] font-semibold">
          {t("careers.eyebrow")}
        </p>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-none mt-5">
          {t("careers.title")}
        </h1>
        <p className="text-[#5C5C59] mt-6 leading-relaxed">{t("careers.subtitle")}</p>

        <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-[#25D366]/10 border border-[#25D366]/30 rounded text-sm text-[#1FAE54]">
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M20.52 3.48A11.88 11.88 0 0012.04 0C5.5 0 .2 5.3.2 11.84c0 2.09.55 4.13 1.6 5.93L0 24l6.36-1.66a11.83 11.83 0 005.68 1.45c6.54 0 11.84-5.3 11.84-11.83 0-3.16-1.23-6.13-3.36-8.48zm-8.47 18.22a9.84 9.84 0 01-5.02-1.38l-.36-.21-3.78.99 1.01-3.69-.24-.38a9.84 9.84 0 01-1.5-5.19c0-5.43 4.42-9.85 9.86-9.85 5.43 0 9.85 4.42 9.85 9.85-.01 5.44-4.43 9.85-9.85 9.85z"/></svg>
          {t("careers.viaWhatsapp")}
        </div>

        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="careers-form">
          <Field label={t("careers.name")}>
            <input required name="name" value={form.name} onChange={onChange} data-testid="cv-name" className={inputCls} />
          </Field>
          <Field label={t("careers.phone")}>
            <input required name="phone" value={form.phone} onChange={onChange} data-testid="cv-phone" className={inputCls} />
          </Field>
          <Field label={t("careers.position")}>
            <select required name="position" value={form.position} onChange={onChange} data-testid="cv-position" className={inputCls}>
              {["hall", "kitchen", "bartender", "sommelier", "other"].map((k) => (
                <option key={k} value={k}>{t(`careers.positions.${k}`)}</option>
              ))}
            </select>
          </Field>
          <Field label={t("careers.message")}>
            <textarea name="message" rows="4" value={form.message} onChange={onChange} data-testid="cv-message" className={inputCls} />
          </Field>

          <p className="text-xs text-[#5C5C59] italic">
            {t("careers.cvNote")}
          </p>

          <button
            type="submit"
            data-testid="cv-submit"
            className="w-full sm:w-auto px-10 py-4 bg-[#25D366] hover:bg-[#1FAE54] text-white transition-all hover:-translate-y-[1px] inline-flex items-center justify-center gap-2.5"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M20.52 3.48A11.88 11.88 0 0012.04 0C5.5 0 .2 5.3.2 11.84c0 2.09.55 4.13 1.6 5.93L0 24l6.36-1.66a11.83 11.83 0 005.68 1.45c6.54 0 11.84-5.3 11.84-11.83 0-3.16-1.23-6.13-3.36-8.48zm-8.47 18.22a9.84 9.84 0 01-5.02-1.38l-.36-.21-3.78.99 1.01-3.69-.24-.38a9.84 9.84 0 01-1.5-5.19c0-5.43 4.42-9.85 9.86-9.85 5.43 0 9.85 4.42 9.85 9.85-.01 5.44-4.43 9.85-9.85 9.85z"/></svg>
            {t("careers.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs tracking-[0.2em] uppercase text-[#5C5C59] font-semibold">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
